import React, { useCallback, useEffect, useState } from 'react';
import authService from '../services/auth.service';

interface Account {
  id: number | string;
  firstName?: string;
  lastName?: string;
  active?: boolean;
}

interface Contractor {
  id: string;
  firstName: string;
  lastName: string;
  company?: string;
}

interface HrGroup {
  id: string;
  name: string;
  description: string;
  color: string;
  userIds: string[];
}

const fieldStyle: React.CSSProperties = {
  background: '#181b1f',
  border: '1px solid #353a40',
  borderRadius: 8,
  color: '#fff',
  padding: '10px 12px',
  width: '100%',
};

const personName = (person: { firstName?: string; lastName?: string }) =>
  `${person.firstName || ''} ${person.lastName || ''}`.trim() || 'Sans nom';

const nameKey = (person: { firstName?: string; lastName?: string }) =>
  personName(person).toLocaleLowerCase('fr').replace(/\s+/g, ' ').trim();

const memberIds = (group: { userIds?: unknown }): string[] => {
  let value = group.userIds;
  if (typeof value === 'string') {
    try { value = JSON.parse(value); } catch { return []; }
  }
  return Array.isArray(value) ? value.map((id) => String(id)) : [];
};

async function hrFetch(path: string, options: RequestInit = {}) {
  const token = authService.getToken();
  const response = await fetch(path, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  });
  if (response.status === 204) return null;
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(body.error || 'Erreur');
  return body;
}

const UserGroupsPanel: React.FC<{ users: Account[] }> = ({ users }) => {
  const [groups, setGroups] = useState<HrGroup[]>([]);
  const [contractors, setContractors] = useState<Contractor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState<HrGroup | null>(null);
  const [creating, setCreating] = useState({ name: '', description: '', color: '#3b82f6', userIds: [] as string[] });

  const accounts = [...users]
    .filter((user) => user.active !== false)
    .sort((a, b) => personName(a).localeCompare(personName(b), 'fr'));

  const accountByName = new Map(accounts.map((account) => [nameKey(account), account]));

  const extraContractors = contractors.filter((contractor) => !accountByName.has(nameKey(contractor)));

  // Un compte et un prestataire externe du même nom sont la même personne.
  const normalizeMemberIds = (ids: string[]) => {
    const next = new Set<string>();
    ids.forEach((raw) => {
      const id = String(raw);
      if (id.startsWith('contractor:')) {
        const contractor = contractors.find((item) => `contractor:${item.id}` === id);
        const account = contractor ? accountByName.get(nameKey(contractor)) : undefined;
        if (account) {
          next.add(String(account.id));
          return;
        }
      }
      next.add(id);
    });
    return Array.from(next);
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [groupData, contractorData] = await Promise.all([
        hrFetch('/api/hr/groups'),
        hrFetch('/api/hr/contractors').catch(() => []),
      ]);
      setGroups((groupData || []).map((group: HrGroup) => ({
        id: group.id,
        name: group.name,
        description: group.description || '',
        color: group.color || '#3b82f6',
        userIds: memberIds(group),
      })));
      setContractors(Array.isArray(contractorData) ? contractorData : []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Impossible de charger les groupes');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleId = (ids: string[], id: string) =>
    ids.includes(id) ? ids.filter((item) => item !== id) : [...ids, id];

  const saveGroup = async (group: HrGroup) => {
    if (!group.name.trim()) return;
    setError(null);
    try {
      await hrFetch(`/api/hr/groups/${group.id}`, {
        method: 'PUT',
        body: JSON.stringify({ ...group, userIds: normalizeMemberIds(group.userIds) }),
      });
      setDraft(null);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Enregistrement impossible');
    }
  };

  const createGroup = async () => {
    if (!creating.name.trim()) return;
    setError(null);
    try {
      await hrFetch('/api/hr/groups', {
        method: 'POST',
        body: JSON.stringify(creating),
      });
      setCreating({ name: '', description: '', color: '#3b82f6', userIds: [] });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Création impossible');
    }
  };

  const deleteGroup = async (group: HrGroup) => {
    if (!window.confirm(`Supprimer le groupe « ${group.name} » ?`)) return;
    setError(null);
    try {
      await hrFetch(`/api/hr/groups/${group.id}`, { method: 'DELETE' });
      if (draft?.id === group.id) setDraft(null);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Suppression impossible');
    }
  };

  const memberLabel = (id: string) => {
    if (id.startsWith('contractor:')) {
      const contractor = contractors.find((item) => `contractor:${item.id}` === id);
      if (!contractor) return null;
      return contractor.company
        ? `${personName(contractor)} (${contractor.company})`
        : personName(contractor);
    }
    const account = users.find((item) => String(item.id) === id);
    return account ? personName(account) : null;
  };

  const chips = (selected: string[], onToggle: (id: string) => void) => (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
      {accounts.map((account) => {
        const id = String(account.id);
        const on = selected.includes(id);
        return (
          <button
            key={id}
            type="button"
            onClick={() => onToggle(id)}
            style={{
              borderRadius: 999,
              padding: '4px 12px',
              cursor: 'pointer',
              fontSize: '0.8rem',
              background: on ? 'rgba(245, 158, 11, 0.18)' : '#181b1f',
              color: on ? '#fbbf24' : '#94a3b8',
              border: `1px solid ${on ? 'rgba(245, 158, 11, 0.45)' : '#353a40'}`,
            }}
          >
            {personName(account)}
          </button>
        );
      })}
      {extraContractors.map((contractor) => {
        const id = `contractor:${contractor.id}`;
        const on = selected.includes(id);
        return (
          <button
            key={id}
            type="button"
            onClick={() => onToggle(id)}
            style={{
              borderRadius: 999,
              padding: '4px 12px',
              cursor: 'pointer',
              fontSize: '0.8rem',
              background: on ? 'rgba(139, 92, 246, 0.18)' : '#181b1f',
              color: on ? '#c4b5fd' : '#94a3b8',
              border: `1px solid ${on ? 'rgba(139, 92, 246, 0.45)' : '#353a40'}`,
            }}
          >
            {personName(contractor)}{contractor.company ? ` (${contractor.company})` : ''}
          </button>
        );
      })}
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {error && (
        <div style={{ color: '#f87171', background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: 8, padding: '10px 12px' }}>
          {error}
        </div>
      )}

      <div style={{ background: '#23262a', border: '1px solid #353a40', borderRadius: 12, padding: 20 }}>
        <div style={{ color: '#fff', fontWeight: 600, marginBottom: 12 }}>Nouveau groupe</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 72px', gap: 10, marginBottom: 10 }}>
          <input
            value={creating.name}
            onChange={(event) => setCreating((prev) => ({ ...prev, name: event.target.value }))}
            placeholder="Nom du groupe"
            style={fieldStyle}
          />
          <input
            type="color"
            value={creating.color}
            onChange={(event) => setCreating((prev) => ({ ...prev, color: event.target.value }))}
            aria-label="Couleur du groupe"
            style={{ ...fieldStyle, padding: 4, cursor: 'pointer' }}
          />
        </div>
        <input
          value={creating.description}
          onChange={(event) => setCreating((prev) => ({ ...prev, description: event.target.value }))}
          placeholder="Description (optionnel)"
          style={{ ...fieldStyle, marginBottom: 12 }}
        />
        <div style={{ color: '#94a3b8', fontSize: '0.8rem', marginBottom: 8 }}>Membres</div>
        {chips(creating.userIds, (id) => setCreating((prev) => ({ ...prev, userIds: toggleId(prev.userIds, id) })))}
        <button
          type="button"
          onClick={createGroup}
          disabled={!creating.name.trim()}
          style={{
            marginTop: 14,
            background: creating.name.trim() ? '#f59e0b' : '#353a40',
            color: creating.name.trim() ? '#181b1f' : '#64748b',
            border: 'none',
            borderRadius: 8,
            padding: '10px 16px',
            fontWeight: 600,
            cursor: creating.name.trim() ? 'pointer' : 'default',
          }}
        >
          Créer le groupe
        </button>
      </div>

      {loading && <div style={{ color: '#64748b' }}>Chargement des groupes…</div>}

      {!loading && groups.length === 0 && (
        <div style={{ color: '#64748b', background: '#23262a', border: '1px solid #353a40', borderRadius: 12, padding: 24, textAlign: 'center' }}>
          Aucun groupe
        </div>
      )}

      {groups.map((group) => {
        const editing = draft?.id === group.id;
        const current = editing && draft ? draft : { ...group, userIds: normalizeMemberIds(group.userIds) };
        const visibleIds = normalizeMemberIds(current.userIds);
        return (
          <div key={group.id} style={{ background: '#23262a', border: '1px solid #353a40', borderLeft: `4px solid ${current.color}`, borderRadius: 12, padding: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                {editing && draft ? (
                  <>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 72px', gap: 10, marginBottom: 8 }}>
                      <input
                        value={draft.name}
                        onChange={(event) => setDraft({ ...draft, name: event.target.value })}
                        style={fieldStyle}
                      />
                      <input
                        type="color"
                        value={draft.color || '#3b82f6'}
                        onChange={(event) => setDraft({ ...draft, color: event.target.value })}
                        aria-label="Couleur du groupe"
                        style={{ ...fieldStyle, padding: 4, cursor: 'pointer' }}
                      />
                    </div>
                    <input
                      value={draft.description}
                      onChange={(event) => setDraft({ ...draft, description: event.target.value })}
                      placeholder="Description"
                      style={fieldStyle}
                    />
                  </>
                ) : (
                  <>
                    <div style={{ color: '#fff', fontWeight: 600 }}>{group.name}</div>
                    {group.description && <div style={{ color: '#94a3b8', fontSize: '0.85rem', marginTop: 4 }}>{group.description}</div>}
                  </>
                )}
                <div style={{ color: '#64748b', fontSize: '0.75rem', marginTop: 8 }}>
                  {visibleIds.length} membre{visibleIds.length > 1 ? 's' : ''}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                {editing && draft ? (
                  <>
                    <button type="button" onClick={() => saveGroup(draft)} style={actionStyle('#f59e0b', '#181b1f')}>Enregistrer</button>
                    <button type="button" onClick={() => setDraft(null)} style={actionStyle('#353a40', '#cbd5e1')}>Annuler</button>
                  </>
                ) : (
                  <>
                    <button type="button" onClick={() => setDraft({ ...group, color: group.color || '#3b82f6', userIds: normalizeMemberIds(group.userIds) })} style={actionStyle('#353a40', '#cbd5e1')}>Modifier</button>
                    <button type="button" onClick={() => deleteGroup(group)} style={actionStyle('transparent', '#f87171', '1px solid rgba(239, 68, 68, 0.5)')}>Supprimer</button>
                  </>
                )}
              </div>
            </div>
            <div style={{ marginTop: 12 }}>
              {editing && draft
                ? chips(draft.userIds, (id) => setDraft({ ...draft, userIds: toggleId(draft.userIds, id) }))
                : (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {visibleIds.map((id) => {
                      const label = memberLabel(id);
                      if (!label) return null;
                      return (
                        <span key={id} style={{ borderRadius: 999, padding: '4px 12px', fontSize: '0.8rem', color: '#cbd5e1', background: '#181b1f', border: '1px solid #353a40' }}>
                          {label}
                        </span>
                      );
                    })}
                  </div>
                )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const actionStyle = (background: string, color: string, border = 'none'): React.CSSProperties => ({
  background,
  color,
  border,
  borderRadius: 8,
  padding: '8px 12px',
  fontSize: '0.8rem',
  fontWeight: 600,
  cursor: 'pointer',
});

export default UserGroupsPanel;
